import {
    BugIcon,
    CheckIcon,
    CircleCheckFilledIcon,
    CircleCrossIcon,
    CirclePauseIcon,
    CloseIcon,
    DisabledIcon,
    DoubleCheckIcon,
    DurationIcon,
    HandIcon,
    QuestionIcon,
} from "@xola/icons";
import { DateFormat, duration, formatDate, JsDateRange, now, toLocalDate } from "common";
import { Dayjs } from "dayjs";
import { maxBy, startCase, uniq } from "lodash";
import type { ComponentType } from "react";
import {
    Conversation,
    DeliveryReport,
    DeliveryReportStatus,
    MessageFormatType,
    Participant,
    Purchase,
    PurchaseItem,
    SelectionState,
} from "types";
import { getGuestPhone } from "@/models/Guest";
import { isExperiencePurchaseItem } from "@/models/PurchaseItem";

export type BadgeColor = "success" | "secondary" | "danger";

export interface PhoneDisplay {
    value: string;
    title: string;
}

const STATUS_COLORS: Record<DeliveryReportStatus, BadgeColor> = {
    [DeliveryReportStatus.PENDING]: "secondary",
    [DeliveryReportStatus.QUEUED]: "secondary",
    [DeliveryReportStatus.SENT]: "secondary",
    [DeliveryReportStatus.DELIVERED]: "success",
    [DeliveryReportStatus.FAILED]: "danger",
    [DeliveryReportStatus.UNDELIVERED]: "danger",
    [DeliveryReportStatus.ERROR]: "danger",
    [DeliveryReportStatus.SUPPRESSED]: "danger",
};

const STATUS_ICONS: Record<DeliveryReportStatus, ComponentType> = {
    [DeliveryReportStatus.PENDING]: CirclePauseIcon,
    [DeliveryReportStatus.QUEUED]: DurationIcon,
    [DeliveryReportStatus.FAILED]: CircleCrossIcon,
    [DeliveryReportStatus.ERROR]: BugIcon,
    [DeliveryReportStatus.SENT]: CheckIcon,
    [DeliveryReportStatus.UNDELIVERED]: DisabledIcon,
    [DeliveryReportStatus.DELIVERED]: CircleCheckFilledIcon,
    [DeliveryReportStatus.SUPPRESSED]: HandIcon,
};

/** Null credits mean the provider has not reported a count yet, so there is nothing knowable to show, not a zero. */
export const getCreditsLabel = (credits: number | null) => {
    return credits === null ? "--" : String(credits);
};

export const getStatusColor = (status: DeliveryReportStatus) => {
    return STATUS_COLORS[status] ?? "secondary";
};

/** Statuses arrive as lowercase enum strings from the API; humanize for display while keeping the raw value elsewhere. */
export const getStatusLabel = (status: DeliveryReportStatus) => {
    return startCase(status.toLowerCase());
};

/**
 * Returns the icon component (not an element) so this stays a plain lookup; the caller renders it. A status the API
 * adds before this map does falls back to a question mark, since rendering `undefined` as a component crashes the page.
 */
export const getStatusIcon = (status: DeliveryReportStatus): ComponentType => {
    return STATUS_ICONS[status] ?? QuestionIcon;
};

// `all` expands to every status, since the array has no separate representation for it.
export const getStatusesFromSelection = (selection: SelectionState): DeliveryReportStatus[] => {
    return selection.all ? Object.values(DeliveryReportStatus) : (selection.selected as DeliveryReportStatus[]);
};

// Set `all` only when every status is selected so Select All can clear the selection.
export const getSelectionFromStatuses = (statuses: DeliveryReportStatus[]): SelectionState => {
    const selected = new Set(statuses);
    const isEveryStatusSelected = Object.values(DeliveryReportStatus).every((status) => selected.has(status));

    return isEveryStatusSelected ? { all: true, selected: [] } : { all: false, selected: statuses };
};

/**
 * `value` carries a plus so ui-kit's `Phone` parses the country instead of assuming its US default; `title` is the
 * number as the API returned it, which is what support pastes into a provider dashboard.
 */
export const getPhoneDisplay = (raw: string | null, normalized: string | null): PhoneDisplay | null => {
    if (!raw && !normalized) {
        return null;
    }

    const title = raw ?? normalized!;
    const value = normalized ? `+${normalized}` : title;

    return { value, title };
};

const findMessage = (conversation: Conversation | undefined, messageId: ID) => {
    return conversation?.messages?.find(({ id }) => id === messageId);
};

export const getMessageText = (conversation: Conversation | undefined, messageId: ID) => {
    const message = findMessage(conversation, messageId);

    return message?.formats?.find(({ type }) => type === MessageFormatType.TEXT)?.body;
};

/**
 * `to[].name` is only populated once the participant's contact info has been resolved, so it is
 * genuinely absent on some messages even after the conversation has loaded; take the first entry
 * that actually carries one rather than assuming `to[0]` always does.
 */
export const getRecipientName = (conversation: Conversation | undefined, messageId: ID) => {
    const message = findMessage(conversation, messageId);

    return message?.to?.find((participant) => participant.name)?.name;
};

export const DELIVERY_REPORT_PAGE_SIZE = 50;

export interface DeliveryReportFilters {
    search: string;
    /** Empty means no status filter, not "match nothing", keeping the array the one honest representation. */
    statuses: DeliveryReportStatus[];
    /** Seller-local calendar day, YYYY-MM-DD. */
    from?: string;
    to?: string;
}

export const buildDeliveryReportParams = (filters: DeliveryReportFilters, sellerId: ID) => {
    const { search, statuses, from, to } = filters;
    const hasCompleteRange = !!from && !!to;
    const hasStatuses = statuses.length > 0;

    return {
        seller: sellerId,
        limit: DELIVERY_REPORT_PAGE_SIZE,
        // Sent raw: normalization happens once, server-side, rather than being duplicated here.
        ...(search && { sentToNormalized: search }),
        // `status[in]` also covers a single selection, so there is one param shape regardless of count.
        ...(hasStatuses && { "status[in]": statuses.join(",") }),
        ...(hasCompleteRange && { "updatedAt[range]": `${from},${to}` }),
    };
};

const isDeliveryReportStatus = (value: string): value is DeliveryReportStatus => {
    return (Object.values(DeliveryReportStatus) as string[]).includes(value);
};

export const parseDeliveryReportFilters = (params: URLSearchParams): DeliveryReportFilters => {
    const statusParam = params.get("status");
    const statuses = statusParam
        ? uniq(statusParam.split(",")).filter((value): value is DeliveryReportStatus => isDeliveryReportStatus(value))
        : [];
    const from = params.get("from");
    const to = params.get("to");

    return {
        search: params.get("search") ?? "",
        statuses,
        ...(from && { from }),
        ...(to && { to }),
    };
};

export const serializeDeliveryReportFilters = ({ search, statuses, from, to }: DeliveryReportFilters) => {
    const params = new URLSearchParams();
    if (search) {
        params.set("search", search);
    }

    if (statuses.length > 0) {
        params.set("status", statuses.join(","));
    }

    if (from) {
        params.set("from", from);
    }

    if (to) {
        params.set("to", to);
    }

    return params.toString();
};

/**
 * The picker hands back a JS date holding a wall-clock day. `now()` reads that wall clock in the seller's timezone,
 * which is the day the admin actually clicked; `dayjs(date)` would read it in the browser's zone and can shift it.
 */
export const toSellerDate = (date: Date) => {
    return formatDate(now(date), DateFormat.DATE_ISO);
};

/** The inverse, for handing the stored strings back to the picker as local JS dates. */
export const getDateRangeValue = ({ from, to }: DeliveryReportFilters): JsDateRange | undefined => {
    if (!from || !to) {
        return undefined;
    }

    return { from: toLocalDate(now(from)).toDate(), to: toLocalDate(now(to)).toDate() };
};

/** Default to the trailing month ending today. */
export const getDefaultDeliveryReportFilters = (): DeliveryReportFilters => {
    const to = now();
    const from = to.subtract(1, "month");

    return {
        search: "",
        statuses: [],
        from: formatDate(from, DateFormat.DATE_ISO),
        to: formatDate(to, DateFormat.DATE_ISO),
    };
};

/** Keep the filter row and table aligned. */
export const DELIVERY_REPORT_GUTTER_CLASS = "px-6";

/**
 * `date` is already a `Dayjs` in the seller's timezone by the time it reaches here, so this stays
 * consistent with the absolute timestamp rendered beside it. The `relativeTime` plugin is already
 * extended app-wide, so `fromNow` is available on every `Dayjs`.
 */
export const getRelativeTime = (date: Dayjs) => {
    return date.fromNow();
};

/** The purchase timeline collapses statuses into the states a seller acts on. */
export enum DeliveryIndicator {
    FAILED = "failed",
    QUEUED = "queued",
    SENT = "sent",
    DELIVERED = "delivered",
}

const INDICATOR_BY_STATUS: Record<DeliveryReportStatus, DeliveryIndicator> = {
    [DeliveryReportStatus.PENDING]: DeliveryIndicator.QUEUED,
    [DeliveryReportStatus.QUEUED]: DeliveryIndicator.QUEUED,
    [DeliveryReportStatus.SENT]: DeliveryIndicator.SENT,
    [DeliveryReportStatus.DELIVERED]: DeliveryIndicator.DELIVERED,
    [DeliveryReportStatus.FAILED]: DeliveryIndicator.FAILED,
    [DeliveryReportStatus.UNDELIVERED]: DeliveryIndicator.FAILED,
    [DeliveryReportStatus.ERROR]: DeliveryIndicator.FAILED,
    [DeliveryReportStatus.SUPPRESSED]: DeliveryIndicator.FAILED,
};

interface DeliveryIndicatorStyle {
    icon: ComponentType<{ className?: string }>;
    className: string;
}

const INDICATOR_STYLES: Record<DeliveryIndicator, DeliveryIndicatorStyle> = {
    [DeliveryIndicator.FAILED]: { icon: CloseIcon, className: "text-danger" },
    [DeliveryIndicator.QUEUED]: { icon: CirclePauseIcon, className: "text-warning" },
    [DeliveryIndicator.SENT]: { icon: CheckIcon, className: "text-gray" },
    [DeliveryIndicator.DELIVERED]: { icon: DoubleCheckIcon, className: "text-success" },
};

/** Worst first, so a single failed recipient is never hidden behind the others' success. */
const INDICATOR_SEVERITY = [
    DeliveryIndicator.FAILED,
    DeliveryIndicator.QUEUED,
    DeliveryIndicator.SENT,
    DeliveryIndicator.DELIVERED,
];

/** A status the API adds before this map does reads as still in flight rather than breaking the item. */
export const getDeliveryIndicator = (status: DeliveryReportStatus) => {
    return INDICATOR_BY_STATUS[status] ?? DeliveryIndicator.QUEUED;
};

export const getIndicatorLabel = (indicator: DeliveryIndicator) => {
    return startCase(indicator);
};

export const getDeliveryIndicatorStyle = (indicator: DeliveryIndicator) => {
    return INDICATOR_STYLES[indicator] ?? INDICATOR_STYLES[DeliveryIndicator.QUEUED];
};

/** A carrier that never confirms leaves a report at `sent` forever, so only a recent update counts as in flight. */
const IN_FLIGHT_WINDOW = duration(5, "minutes");

export const isDeliveryInFlight = ({ status, updatedAt }: DeliveryReport) => {
    const indicator = getDeliveryIndicator(status);
    const isRecent = now().diff(updatedAt) < IN_FLIGHT_WINDOW;
    const isAwaitingCarrier = indicator === DeliveryIndicator.QUEUED || indicator === DeliveryIndicator.SENT;

    return isAwaitingCarrier && isRecent;
};

/** Provider event times beat `updatedAt`, which is when Xola wrote the callback and can lag by seconds. */
export const getDeliveryTime = (report: DeliveryReport) => {
    const { status, queuedAt, sentAt, deliveredAt, createdAt, updatedAt } = report;
    const statusTimes: Partial<Record<DeliveryReportStatus, Dayjs | null | undefined>> = {
        [DeliveryReportStatus.PENDING]: createdAt,
        [DeliveryReportStatus.QUEUED]: queuedAt,
        [DeliveryReportStatus.SENT]: sentAt,
        [DeliveryReportStatus.DELIVERED]: deliveredAt,
    };

    return statusTimes[status] ?? updatedAt;
};

export const formatDeliveryTime = (date: Dayjs) => {
    const isThisYear = date.year() === now().year();
    return isThisYear
        ? `${formatDate(date, DateFormat.MONTH_DATE_SHORT)}, ${formatDate(date, DateFormat.TIME)}`
        : formatDate(date, DateFormat.DATE_TIME_SHORT);
};

/** Errors are stored as `name (code): message`; only the message reads as plain English to a seller. */
export const getDeliveryErrorMessage = (error: string) => {
    const separatorIndex = error.indexOf(": ");
    return separatorIndex === -1 ? error : error.slice(separatorIndex + 2);
};

export const getDeliveryStatusText = ({ status, error }: DeliveryReport) => {
    const indicator = getDeliveryIndicator(status);
    const isFailed = indicator === DeliveryIndicator.FAILED;
    if (isFailed) {
        const errorMessage = error ? getDeliveryErrorMessage(error) : "";
        return errorMessage || getStatusLabel(status);
    }

    // Labelled by indicator, not status, so a pending or unknown status still reads "Queued".
    return getIndicatorLabel(indicator);
};

export const getRolledUpDelivery = (reports: DeliveryReport[]) => {
    const indicators = new Set(reports.map(({ status }) => getDeliveryIndicator(status)));
    const indicator = INDICATOR_SEVERITY.find((candidate) => indicators.has(candidate));
    if (!indicator) {
        return undefined;
    }

    const times = reports
        .filter(({ status }) => getDeliveryIndicator(status) === indicator)
        .map((report) => getDeliveryTime(report));
    const time = maxBy(times, (date) => date.valueOf())!;

    return { indicator, time };
};

/**
 * `sentTo` is the traveler phone as stored, so match exactly; re-normalizing would drift from the server's reading.
 * Purchaser phone is the last match: without a questionnaire no guest has a phone, and the organizer's may not hold one.
 */
export const getRecipientNameByPhone = (
    item: PurchaseItem,
    sentTo: string,
    purchaser?: Pick<Purchase, "phone" | "customerName">,
) => {
    if (!isExperiencePurchaseItem(item)) {
        return undefined;
    }

    const { organizer, guests = [] } = item;
    if (organizer?.name && organizer.phone === sentTo) {
        return organizer.name;
    }

    const guestName = guests.find((guest) => guest.name && getGuestPhone(guest) === sentTo)?.name;
    if (guestName) {
        return guestName;
    }

    const isPurchaserPhone = !!purchaser && purchaser.phone === sentTo;
    return isPurchaserPhone ? organizer?.name ?? purchaser.customerName : undefined;
};

export interface DeliveryRecipient {
    report: DeliveryReport;
    name?: string;
    phone: PhoneDisplay | null;
}

export const getDeliveryRecipients = (
    item: PurchaseItem,
    reports: DeliveryReport[],
    purchaser?: Pick<Purchase, "phone" | "customerName">,
): DeliveryRecipient[] => {
    return reports.map((report) => {
        const { sentTo, sentToNormalized } = report;
        return {
            report,
            name: getRecipientNameByPhone(item, sentTo, purchaser),
            phone: getPhoneDisplay(sentTo, sentToNormalized),
        };
    });
};

const getParticipantPhone = (item: PurchaseItem, participantId: ID) => {
    if (!isExperiencePurchaseItem(item)) {
        return undefined;
    }

    const { organizer, guests = [] } = item;
    if (organizer?.user?.id === participantId) {
        return organizer.phone;
    }

    const guest = guests.find(({ traveler }) => traveler?.id === participantId);
    return guest ? getGuestPhone(guest) : undefined;
};

/**
 * Recipients with no report (number unparseable at send time, or past the fetched page) must stay in the "To" badge.
 * Participants carry no phone and their name can differ from the guest's, so match on either the phone or the name.
 */
export const getUnreportedRecipients = (
    item: PurchaseItem,
    recipients: Participant[],
    reports: DeliveryReport[],
    purchaser?: Pick<Purchase, "phone" | "customerName">,
) => {
    const reportedPhones = new Set(reports.map(({ sentTo }) => sentTo));
    const reportedNames = new Set(reports.map(({ sentTo }) => getRecipientNameByPhone(item, sentTo, purchaser)));

    return recipients.filter(({ id, name }) => {
        const phone = getParticipantPhone(item, id);
        const isReported = reportedNames.has(name) || (!!phone && reportedPhones.has(phone));

        return !!name && !isReported;
    });
};
